import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { LANGUAGES, legalTranslations } from "../translations/legal";
import { logo } from "../assets";
import "./LegalPageLayout.css";

const THEME_KEY = "lineo-theme";
const LANG_KEY = "lineo-lang";

function getInitialTheme() {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === "light" || saved === "dark") return saved;
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches)
    return "dark";
  return "light";
}

function getInitialLang() {
  const saved = localStorage.getItem(LANG_KEY);
  const validCodes = LANGUAGES.map((l) => l.code);
  return validCodes.includes(saved) ? saved : "tr";
}

export default function LegalPageLayout({ children, titleKey, renderContent }) {
  const [theme, setTheme] = useState(getInitialTheme);
  const [lang, setLang] = useState(getInitialLang);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const t = legalTranslations[lang] || legalTranslations.tr;
  const isRtl = lang === "ar";

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <div
      className={`legal-page ${isRtl ? "legal-page--rtl" : ""}`}
      data-theme={theme}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <header className="legal-header">
        <Link to="/" className="legal-logo">
          <img src={logo} alt="lineo" className="legal-logo-img" />
        </Link>
        <nav className="legal-nav">
          <div
            className={`lang-selector ${langOpen ? "lang-selector--open" : ""}`}
            ref={langRef}
          >
            <button
              type="button"
              className="lang-trigger"
              onClick={() => setLangOpen((o) => !o)}
              aria-expanded={langOpen}
              aria-haspopup="listbox"
              aria-label="Dil seçin"
            >
              <span className="lang-trigger-flag">{currentLang.flag}</span>
              <span className="lang-trigger-name">{currentLang.name}</span>
              <span className="lang-trigger-arrow">▼</span>
            </button>
            {langOpen && (
              <ul
                className="lang-dropdown"
                role="listbox"
                aria-label="Dil listesi"
              >
                {LANGUAGES.map((l) => (
                  <li key={l.code} role="option">
                    <button
                      type="button"
                      className={`lang-option ${lang === l.code ? "lang-option--active" : ""}`}
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                    >
                      <span>{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="theme-toggle"
            onClick={toggleTheme}
            title={theme === "light" ? t.themeToggleLight : t.themeToggleDark}
            aria-label={
              theme === "light" ? t.themeToggleLight : t.themeToggleDark
            }
          >
            {theme === "light" ? "🌙" : "☀️"}
          </button>
          <Link to="/" className="nav-link nav-link--primary">
            {t.backToHome}
          </Link>
        </nav>
      </header>

      <main className="legal-main">
        <div className="legal-content">
          <h1 className="legal-title">{t[titleKey]}</h1>
          {typeof renderContent === "function" ? renderContent(lang) : children}
        </div>
      </main>

      <footer className="legal-footer">
        <div className="legal-footer-links">
          <Link to="/privacy-policy">{t.privacyPolicy}</Link>
          <span className="footer-sep">•</span>
          <Link to="/terms-of-use">{t.termsOfService}</Link>
          <span className="footer-sep">•</span>
          <Link to="/cookie-policy">{t.cookiePolicy}</Link>
          <span className="footer-sep">•</span>
          <Link to="/account-deletion">{t.accountDeletion}</Link>
        </div>
        <p>
          © {new Date().getFullYear()} lineo. {t.footerRights}
        </p>
      </footer>
    </div>
  );
}
